import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Skeleton } from 'components/ui/skeleton';
import { Check, Loader2, X, Phone } from 'lucide-react';
import { WelcomeDialog } from 'components/blocks/welcome-dialog/welcome-dialog';
import ErrorAlert from 'components/blocks/error-alert/error-alert';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import {
    useSubscriptionPlans,
    useSubscribeFreePlan
} from 'features/subscription/hooks/use-subscription';
import { useSubscriptionStore } from 'state/store/subscription';
import {
    SubscriptionPlan as APISubscriptionPlan
} from 'features/subscription/services/subscription-service';
import { cn } from 'lib/utils';
import subBackground from 'assets/images/sub_background.svg';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { PLANS, PURCHASE_CHANNEL } from 'constant/plan';
import { useConfig } from 'hooks/use-config';
import { useLanguageContext } from 'i18n/language-context';
import { useCommonStore } from 'state/store/common/useCommonStore';
import ContactModal from './contact-sales-modal';
import { useContactSales } from './use-subscription';
import { processFeatureDisplay } from 'pages/services/utilityService';

type PlanType = 'personal' | 'business';

// Helper function to transform API plan to UI plan format
const transformApiPlanToUiPlan = (apiPlan: APISubscriptionPlan) => {
    const baseItem = apiPlan.items.find(item => item.baseItem) || apiPlan.items[0];

    // Simple billing period conversion
    const getBillingPeriod = (interval: number, intervalUnit: number): string => {
        const units = ['day', 'week', 'month', 'year'];
        const unit = units[intervalUnit] || 'month';

        switch (unit) {
            case 'day': return 'Daily';
            case 'week': return 'Weekly';
            case 'month': return 'Monthly';
            case 'year': return 'Annually';
            default: return 'Monthly';
        }
        return `${interval} ${unit}s`;
    };



    return {
        id: apiPlan.itemId,
        name: apiPlan.name,
        price: baseItem.planPrice,
        totalTax: baseItem.totalTax,
        totalDiscount: baseItem.totalDiscount,
        totalPrice: baseItem.totalPrice,
        subTotalPrice: baseItem.subTotalPrice,
        currency: apiPlan.currency,
        period: getBillingPeriod(baseItem.price.paymentInterval, baseItem.price.paymentIntervalUnit),
        description: apiPlan.description,
        planType: apiPlan.business ? 'business' as const : 'personal' as const,
        buttonText: apiPlan.free ? t('START_FREE_WITH_7_DAY_TRIAL') :
            apiPlan.name?.toUpperCase() === 'ENTERPRISE' ? 'CONTACT_SALES' :
                `${t('GET')} ${t(apiPlan.name || '').toUpperCase()}`,
        isPopular: false, // Will be set based on order
        isCurrentPlan: false, // Will be set based on current subscription
        features: apiPlan.features.filter(f => f.active).map(f => {
            return {
                translationKey: f.translationKey,
                active: f.active,
                value: f.value,
            }
        }),
        // Store original API plan for subscription creation
        _originalApiPlan: apiPlan,
        _baseItem: baseItem
    };
};

type UiPlan = ReturnType<typeof transformApiPlanToUiPlan>;

interface PlanCardProps {
    plan: UiPlan;
    onSubscribe: (planId: string) => void;
    isLoading?: boolean;
    setOpen?: (open: boolean) => void;
}

function PlanCard({ plan, onSubscribe, isLoading = false, setOpen }: PlanCardProps) {
    const isPrimary = plan.isPopular;
    const isCustomPricing = typeof plan.price === 'string';
    const isCurrentPlan = plan.isCurrentPlan;
    const isEnterprise = plan.name.toUpperCase() === 'ENTERPRISE';

    // Local price formatting function
    const formatPrice = (price: number, currency: string): string => {
        return `${price.toFixed(2)} ${currency}`;
    };

    return (
        <Card className={cn(
            "relative h-full w-[316px] transition-all duration-300 hover:shadow-lg",
            "bg-card border-border dark:bg-card dark:border-border",
            isPrimary ? "border-primary shadow-lg dark:border-primary" : "",
            isCurrentPlan ? "border-success-green shadow-lg dark:border-success-green bg-success-green/5" : ""
        )}>
            {isPrimary && !isCurrentPlan && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-primary text-white px-4 py-1 hover:bg-primary">
                        {t('POPULAR_PLAN')}
                    </Badge>
                </div>
            )}
            {isCurrentPlan && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-success-green text-white px-4 py-1 hover:bg-success-green">
                        {t('CURRENT_PLAN')}
                    </Badge>
                </div>
            )}

            <CardHeader className="pb-6 md:pb-1">
                <CardTitle className="text-lg font-semibold text-foreground">
                    {t(plan.name).toUpperCase()}
                </CardTitle>
                <div className="flex items-end gap-2">
                    {isEnterprise ? (
                        <div className="flex items-center gap-2 pb-1">
                            <Phone className="w-5 h-5 text-primary" />
                            <span className="text-lg font-medium text-foreground">
                                {t('CONTACT_FOR_PRICING') || 'Contact for Pricing'}
                            </span>
                        </div>
                    ) : isCustomPricing ? (
                        <span className="text-2xl font-bold text-foreground">
                            {plan.price}
                        </span>
                    ) : (
                        <>
                            <span className="text-2xl font-bold text-foreground">
                                {formatPrice(Number(plan.totalPrice), plan.currency)}
                            </span>
                            <span className="text-sm text-muted-foreground">
                                /{t(plan.period.toUpperCase()).toUpperCase()}
                            </span>
                        </>
                    )}
                </div>
                <p className="text-xs text-muted-foreground leading-5 line-clamp-2 overflow-hidden h-10">
                    {t(plan.description.toUpperCase())}
                </p>
            </CardHeader>

            <CardContent className="space-y-4">
                <Button
                    className={cn(
                        "w-full font-bold transition-all duration-200",
                        isCurrentPlan
                            ? "bg-muted text-muted-foreground cursor-not-allowed"
                            : isPrimary || plan.name === 'ENTERPRISE'
                                ? "bg-primary-600 hover:bg-primary-700"
                                : "bg-background border border-primary text-primary hover:bg-accent hover:text-accent-foreground"
                    )}
                    // onClick={() => !isCurrentPlan && onSubscribe(plan.id)}
                    onClick={() => {
                        if (plan.name.toUpperCase() === 'ENTERPRISE') {
                            setOpen?.(true);
                        } else {
                            onSubscribe(plan.id);
                        }
                    }}
                    disabled={isLoading || isCurrentPlan}
                >
                    <span className="truncate">
                        {isLoading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {t('PROCESSING')}...
                            </>
                        ) : isCurrentPlan ? (
                            t('CURRENT_PLAN')
                        ) : (
                            plan.buttonText === 'CONTACT_SALES' ? (t('CONTACT_SALES') || 'Contact Sales') : plan.buttonText
                        )}
                    </span>
                </Button>

                <div className="space-y-3">
                    {plan.features.map((feature, index) => {
                        const { translationKey, displayValue } = processFeatureDisplay(feature, plan.features);

                        return (
                            <div key={index} className="flex items-center gap-3">
                                <div className="w-4 h-4 flex-shrink-0">
                                    <Check className="w-4 h-4 text-primary" />
                                </div>
                                <span className="text-sm text-foreground leading-5">
                                    {t(translationKey, { value: displayValue })}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
}

interface TabToggleProps {
    activeTab: PlanType;
    onTabChange: (tab: PlanType) => void;
    isOrgAdmin?: boolean;
}

function TabToggle({ activeTab, onTabChange, isOrgAdmin = false }: TabToggleProps) {
    return (
        <div className="bg-card border border-border p-1 rounded-lg inline-flex">
            <button
                className={cn(
                    "px-3 py-1.5 rounded text-sm font-semibold transition-colors",
                    activeTab === 'personal'
                        ? "bg-primary-600 text-primary-foreground"
                        : isOrgAdmin
                            ? "bg-transparent text-muted-foreground/50 cursor-not-allowed"
                            : "bg-transparent text-muted-foreground hover:text-foreground"
                )}
                onClick={() => !isOrgAdmin && onTabChange('personal')}
                disabled={isOrgAdmin}
            >
                {t('PERSONAL')}
            </button>
            <button
                className={cn(
                    "px-3 py-1.5 rounded text-sm font-semibold",
                    activeTab === 'business'
                        ? "bg-primary-600 text-primary-foreground"
                        : "bg-transparent text-muted-foreground hover:text-foreground"
                )}
                onClick={() => onTabChange('business')}
            >
                {t('BUSINESS')}
            </button>
        </div>
    );
}

export function SubscriptionPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<PlanType>('personal');
    const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
    const [showMobileSubscriptionWarning, setShowMobileSubscriptionWarning] = useState(false);
    const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
    const { t } = useTranslation();
    const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
    const user = useCommonStore((state) => state.user);
    const [open, setOpen] = useState(false);

    // Initialize activeTab from URL parameter on mount
    useEffect(() => {
        const tabParam = searchParams.get('tab') as PlanType;

        // Force business tab for org admins
        if (isOrgAdmin) {
            setActiveTab('business');
            if (tabParam !== 'business') {
                setSearchParams({ tab: 'business' }, { replace: true });
            }
        } else if (tabParam === 'personal' || tabParam === 'business') {
            setActiveTab(tabParam);
        } else {
            // Default to personal and set URL parameter
            setSearchParams({ tab: 'personal' }, { replace: true });
        }
    }, [searchParams, setSearchParams, isOrgAdmin]);

    // Handle tab change with URL update
    const handleTabChange = (tab: PlanType) => {
        // Prevent switching to personal tab if user is org admin
        if (isOrgAdmin && tab === 'personal') {
            return;
        }
        setActiveTab(tab);
        setSearchParams({ tab }, { replace: true });
    };

    // Simple navigation back to source route
    const handleNavigateBack = () => {
        // Get the source route from URL parameter
        const fromRoute = searchParams.get('from');

        // Always navigate back to the source route, default to /chat if not available
        if (fromRoute) {
            // Decode the route parameter
            navigate(decodeURIComponent(fromRoute));
        } else {
            navigate('/chat');
        }
    };

    // Fetch subscription plans
    const {
        data: apiResponse,
        isLoading: isPlansLoading,
        error: plansError,
    } = useSubscriptionPlans();

    // Get current plan from global store
    const { currentPlan, setCurrentPlan } = useSubscriptionStore();

    // Subscribe to free plan mutation
    const [selectedFreePlanId, setSelectedFreePlanId] = useState<string | null>(null);
    const { configurationSettings } = useConfig();
    const { currentLanguage } = useLanguageContext();
    const { mutateAsync: contactSales } = useContactSales();

    const subscribeFreePlanMutation = useSubscribeFreePlan({
        onSuccess: (response) => {
            if (response.isSuccess && selectedFreePlanId) {
                // Find the free plan and update the store
                const freePlan = apiResponse?.data?.find(p => p.itemId === selectedFreePlanId && p.free);
                if (freePlan) {
                    setCurrentPlan(freePlan);
                }
                // Show welcome dialog instead of direct navigation
                setShowWelcomeDialog(true);
                // Reset selected plan
                setSelectedFreePlanId(null);
            }
        },
        onError: (error) => {
            console.error('Failed to subscribe to free plan:', error);
            setSelectedFreePlanId(null);
            // Could add toast notification here if available
        }
    });

    const handleContactSales = () => {
        const languageCode = currentLanguage.split('-')[0];
        if (languageCode === 'de') {
            window.open(configurationSettings.paths.subscriptionSupportUrlDE, '_blank');
        } else {
            window.open(configurationSettings.paths.subscriptionSupportUrlEN, '_blank');
        }
    };

    // Helper function to process subscription (free or paid)
    const processSubscription = (planId: string) => {
        const selectedApiPlan = apiResponse?.data?.find(p => p.itemId === planId);
        if (selectedApiPlan) {
            if (selectedApiPlan.free) {
                setSelectedFreePlanId(planId);
                subscribeFreePlanMutation.mutate();
            } else {
                const activePlanItems = selectedApiPlan.items.filter(item => item.active) || [];
                const baseItem = activePlanItems.find(item => item.baseItem) || activePlanItems[0];

                if (!baseItem) {
                    console.error('No active plan items found for plan:', planId);
                    return;
                }

                // Get the 'from' parameter to pass along to checkout
                const fromRoute = searchParams.get('from');
                const checkoutUrl = fromRoute
                    ? `/checkout?from=${encodeURIComponent(fromRoute)}`
                    : '/checkout';

                navigate(checkoutUrl, {
                    state: {
                        planId: selectedApiPlan.itemId,
                        planName: selectedApiPlan.title,
                        price: baseItem.planPrice,
                        currency: selectedApiPlan.currency,
                        subscriptionPlan: selectedApiPlan,
                        planItems: activePlanItems,
                        originalSource: fromRoute,
                        currentTab: activeTab,
                    }
                });
            }
        }
    };

    const handleSubscribe = (planId: string) => {
        const selectedApiPlan = apiResponse?.data?.find(p => p.itemId === planId);
        if (selectedApiPlan) {
            // Check if the plan is Enterprise
            if (selectedApiPlan.name.toUpperCase() === 'ENTERPRISE') {
                // Open contact sales link in new tab
                handleContactSales();
                return;
            }

            // Check if user has existing mobile app subscription (purchaseChannel != 0)
            if (currentPlan && currentPlan.purchaseChannel != PURCHASE_CHANNEL.WEB && currentPlan.name !== PLANS.FREE && currentPlan.name !== PLANS.NOPLAN) {
                // Store the pending plan ID and show warning modal
                setPendingPlanId(planId);
                setShowMobileSubscriptionWarning(true);
                return;
            }

            // Process subscription normally
            processSubscription(planId);
        }
    };

    // Handle mobile subscription warning modal actions
    const handleMobileSubscriptionConfirm = () => {
        setShowMobileSubscriptionWarning(false);
        if (pendingPlanId) {
            processSubscription(pendingPlanId);
            setPendingPlanId(null);
        }
    };

    // Transform API plans to UI format and filter by type
    const currentPlans = React.useMemo(() => {
        if (!apiResponse?.data) return [];

        // Filter by active and plan type
        const filteredPlans = apiResponse.data.filter(plan =>
            plan.active && plan.business === (activeTab === 'business')
        );

        const uiPlans = filteredPlans.map(transformApiPlanToUiPlan); // filter the baseItem of the plan

        // Set most popular plan (order 1)
        const popularPlan = uiPlans.find(p => p._originalApiPlan.order === 1);
        if (popularPlan) {
            popularPlan.isPopular = true;
        }

        // Mark current plan as active
        if (currentPlan) {
            const currentUiPlan = uiPlans.find(p => p.id === currentPlan.itemId);
            if (currentUiPlan) {
                currentUiPlan.isCurrentPlan = true;
                // Don't show "Most Popular" badge for current plan
                currentUiPlan.isPopular = false;
            }
        }

        return uiPlans.filter(plan => plan.name != PLANS.UNLIMITED);
    }, [apiResponse, activeTab, currentPlan]);

    interface ContactFormData {
        name: string;
        company: string;
        email: string;
        phone?: string;
        message: string;
    }

    const handleSubmit = async (data: ContactFormData) => {
        const payload = {
            "language": currentLanguage,
            "message": data?.message,
            "organizationName": data?.company,
            "contactName": data?.name,
            "phone": data?.phone,
            "emailTo": configurationSettings.paths.salesContactMailTo,
            "email": data?.email,
        }

        try {
            const response = await contactSales(payload);
            return response;
        } catch (error) {
            console.error(error);
        }
    };


    return (
        <div className="min-h-screen bg-background flex justify-center w-full relative">
            {/* Close button */}
            <button
                onClick={handleNavigateBack}
                className="absolute top-4 right-4 z-20 p-2 text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-accent"
                aria-label="Close subscription page"
            >
                <X size={24} />
            </button>

            {/* Background decoration with SVG */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute inset-0 flex items-center justify-center">
                    <img
                        src={subBackground}
                        alt=""
                        className="w-full h-auto max-w-none"
                        style={{ minWidth: '1440px' }}
                    />
                </div>
            </div>

            <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="text-center mb-12 space-y-4">
                    <h1 className="text-3xl font-bold text-foreground leading-tight">
                        {t('SMARTER_WORK_STARTS')}
                    </h1>
                    <p className="text-xl text-muted-foreground leading-9 max-w-2xl mx-auto">
                        {t('START_YOUR_SEVEN')}
                    </p>
                </div>

                {/* Tab Toggle */}
                <div className="flex justify-center mb-12">
                    <TabToggle activeTab={activeTab} onTabChange={handleTabChange} isOrgAdmin={isOrgAdmin} />
                </div>



                {/* Plans Grid - Centered or Loading/Error states */}
                <div className="flex justify-center mb-12">
                    {isPlansLoading ? (
                        <div className={cn(
                            "grid gap-6 justify-items-center",
                            // Use expected grid layout for the active tab
                            activeTab === 'personal'
                                ? "grid-cols-1 md:grid-cols-3 max-w-6xl"
                                : "grid-cols-1 md:grid-cols-2 max-w-4xl"
                        )}>
                            {Array.from({ length: activeTab === 'personal' ? 3 : 2 }).map((_, i) => (
                                <Card key={i} className="h-[360px] w-full max-w-sm">
                                    <CardHeader>
                                        <Skeleton className="h-6 w-32" />
                                        <Skeleton className="h-8 w-24" />
                                        <Skeleton className="h-4 w-48" />
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <Skeleton className="h-10 w-full" />
                                        <div className="space-y-2">
                                            {[1, 2, 3, 4].map((j) => (
                                                <Skeleton key={j} className="h-4 w-full" />
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : plansError ? (
                        <div className="max-w-lg">
                            <ErrorAlert
                                isError={true}
                                message="Failed to load subscription plans. Please try again later."
                            />
                        </div>
                    ) : currentPlans.length === 0 ? (
                        <div className="max-w-lg p-4 border rounded-lg bg-muted">
                            <p className="text-sm text-muted-foreground text-center">
                                {t('NO_SUBSCRIPTION_PLANS_FOR')} {activeTab} {t('USERS')}.
                            </p>
                        </div>
                    ) : (
                        <div className={cn(
                            "grid gap-6 justify-items-center",
                            // Dynamic grid based on plan count
                            currentPlans.length === 1
                                ? "grid-cols-1 max-w-sm"
                                : currentPlans.length === 2
                                    ? "grid-cols-1 md:grid-cols-2 max-w-4xl"
                                    : currentPlans.length === 3
                                        ? "grid-cols-1 md:grid-cols-3 max-w-6xl"
                                        : currentPlans.length === 4
                                            ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 max-w-7xl"
                                            : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 max-w-7xl"
                        )}>
                            {currentPlans.map((plan, index) => (
                                <div
                                    key={plan.id}
                                    className={cn(
                                        "transition-all duration-700 ease-out",
                                        "transform"
                                    )}
                                    style={{
                                        transitionDelay: `${index * 100}ms`
                                    }}
                                >
                                    <PlanCard
                                        plan={plan}
                                        onSubscribe={handleSubscribe}
                                        isLoading={plan._originalApiPlan.free && selectedFreePlanId === plan.id ? subscribeFreePlanMutation.isPending : false}
                                        setOpen={setOpen}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer text */}
                {/* <div className="text-center mt-12">
                    <p className="text-muted-foreground">
                        All plans include a 14-day free trial. Cancel anytime.
                    </p>
                </div> */}
            </div>

            {/* Welcome Dialog */}
            <WelcomeDialog
                open={showWelcomeDialog}
                onOpenChange={setShowWelcomeDialog}
                onGetStarted={() => window.location.href = '/chat'}
            />

            {/* Mobile Subscription Warning Modal */}
            <ConfirmationModal
                open={showMobileSubscriptionWarning}
                onOpenChange={(open) => {
                    setShowMobileSubscriptionWarning(open);
                    if (!open) {
                        setPendingPlanId(null);
                    }
                }}
                title={t('MOBILE_SUBSCRIPTION_WARNING_TITLE') || 'Existing Mobile Subscription'}
                description={
                    t('MOBILE_SUBSCRIPTION_WARNING_MESSAGE') ||
                    'You currently have an active subscription purchased through our mobile app. Purchasing this plan will create a separate subscription and you will be charged for both. To avoid double billing, please cancel your mobile app subscription first through the app store settings.'
                }
                onConfirm={handleMobileSubscriptionConfirm}
                confirmText={t('CONFIRM_NEW_PURCHASE') || 'Confirm New Purchase'}
                cancelText={t('CANCEL') || 'Cancel'}
            />

            <ContactModal
                isOpen={open}
                onClose={() => setOpen(false)}
                onSubmit={handleSubmit}
                companyRequired={false}
                initialData={{
                    name: user ? `${user.firstName} ${user.lastName}`.trim() : "",
                    email: user?.email || "",
                    phone: user?.phoneNumber || "",
                }}
            />
        </div>
    );
} 